import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  Fade,
} from "@mui/material";

import searchPng from "../../media/Search_PNG.png";
import sciencePng from "../../media/Science_PNG.png";
import savePng from "../../media/Mobile_notification_PNG.png";
import HomeFooter from "../shared/HomeFooter";
import Navigation from "../shared/Navigation";

function About() {
  React.useEffect(() => {
    document.title = "About - ShieldShot";
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <Navigation />
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #2997ff 0%, #1976d2 100%)",
          py: { xs: 8, md: 12 },
          color: "white",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                color: "white",
              }}
            >
              How ShieldShot Works
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 400,
                maxWidth: 800,
                mx: "auto",
                opacity: 0.9,
                fontSize: { xs: "1.2rem", md: "1.5rem" },
                color: "white",
              }}
            >
              Four easy steps to automate distribution and protect your content
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 4, md: 6 } }}>
          {[
            {
              image: searchPng,
              title: "Track unauthorized sharing",
              description: "Monitor all downloads with detailed analytics. Get instant notifications about suspicious activity so you can take immediate action to protect your content. Our advanced tracking system provides you with comprehensive insights into who is accessing your content, when, and from where."
            },
            {
              image: savePng,
              title: "Store and organize securely",
              description: "Never lose track of your photos again. Our secure cloud storage lets you upload from any device and organize your content effortlessly. With military-grade encryption and redundant backups, your content is safe and always accessible when you need it."
            },
            {
              image: sciencePng,
              title: "Advanced features coming soon",
              description: "Built by creators for creators. We're continuously developing new features to make content distribution more convenient and secure. Our roadmap includes customer accounts, shared uploading, enhanced download controls, and much more."
            }
          ].map((feature, index) => (
            <Fade in timeout={1000 + index * 300} key={feature.title}>
              <Card
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: index % 2 === 0 ? "row" : "row-reverse" },
                  alignItems: "center",
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  background: "white",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {/* Image Container */}
                <Box
                  sx={{
                    flex: "0 0 auto",
                    width: { xs: "100%", md: 200 },
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: { xs: 3, md: 0 },
                    mr: { xs: 0, md: index % 2 === 0 ? 4 : 0 },
                    ml: { xs: 0, md: index % 2 === 0 ? 0 : 4 },
                  }}
                >
                  <Box
                    component="img"
                    src={feature.image}
                    alt={feature.title}
                    sx={{
                      height: { xs: 120, md: 140 },
                      width: "auto",
                      maxWidth: "100%",
                    }}
                  />
                </Box>

                {/* Content Container */}
                <Box
                  sx={{
                    flex: 1,
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#2997ff",
                      fontWeight: 700,
                      mb: 2,
                      fontSize: { xs: "1.5rem", md: "2rem" },
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "grey.700",
                      lineHeight: 1.6,
                      fontSize: { xs: "1rem", md: "1.1rem" },
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Card>
            </Fade>
          ))}
        </Box>

        {/* How It Works Section */}
        <Box sx={{ mt: { xs: 8, md: 12 }, textAlign: "center" }}>
          <Typography
            variant="h3"
            sx={{
              color: "#2997ff",
              fontWeight: 700,
              mb: 4,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            The ShieldShot Process
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "grey.600",
              mb: 6,
              maxWidth: 700,
              mx: "auto",
              fontSize: { xs: "1.1rem", md: "1.25rem" },
            }}
          >
            When you schedule your distribution, just tell us the tier and we handle the rest.
          </Typography>
          
          <Card
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              background: "white",
              textAlign: "left",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: "#2997ff",
                fontWeight: 700,
                mb: 3,
              }}
            >
              Steganography Protection
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "grey.700",
                lineHeight: 1.6,
                fontSize: "1.1rem",
                mb: 3,
              }}
            >
              Every photo uploaded to ShieldShot is protected using advanced steganography techniques. 
              This means your copyright information and the recipient's details are invisibly embedded 
              into each image, making it impossible for unauthorized users to share your content without 
              being traced back to the source.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "grey.700",
                lineHeight: 1.6,
                fontSize: "1.1rem",
              }}
            >
              This protection survives compression, resizing, and most editing attempts, ensuring your 
              content remains traceable no matter how it's shared or modified.
            </Typography>
          </Card>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ mt: 0, backgroundColor: "#f1f1f1", py: 0 }}>
        <HomeFooter />
      </Box>

    </Box>
  );
}

export default About;
